import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useInvalidate, useList } from '@refinedev/core';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { humanizeEnum } from '../../../components/Form.js';
import { api, ApiError } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';
import { fetchSmsConsentList, type SmsConsentDto } from '../../../lib/sms-consent.js';
import { STATUS_PILL_CLASS } from '../leases/LeaseList.js';

export interface TenantRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  leaseCount: number;
  hasPortalAccount: boolean;
  pendingInviteSentAt: string | null;
  currentLease: { id: string; unitLabel: string; propertyName: string; status: string } | null;
  achAccess: { revokedAt: string | null };
  archivedAt: string | null;
}

export function TenantList() {
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const canWrite = useCan('tenants:write');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const pageSize = 25;

  const { data, isLoading, error } = useList<TenantRow>({
    resource: 'tenants',
    pagination: { current: page, pageSize },
    filters: [
      { field: 'search', operator: 'contains', value: search || undefined },
      { field: 'includeArchived', operator: 'eq', value: includeArchived ? 'true' : undefined },
    ],
  });

  // A separate, messaging:read-gated endpoint (see SmsConsentDto's own doc
  // comment in modules/tenants/service.ts for why this can't ride along on
  // the tenants list above) — same page/search/includeArchived params, so
  // rows line up by tenantId. Best-effort: a failed fetch just means no SMS
  // pill shows, not a broken tenant list.
  const [smsConsentById, setSmsConsentById] = useState<Record<string, SmsConsentDto>>({});
  useEffect(() => {
    let cancelled = false;
    fetchSmsConsentList({ page, pageSize, search: search || undefined, includeArchived })
      .then((result) => {
        if (cancelled) return;
        const map: Record<string, SmsConsentDto> = {};
        for (const row of result.data) map[row.tenantId] = row;
        setSmsConsentById(map);
      })
      .catch(() => {
        /* best-effort, see comment above */
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, search, includeArchived]);

  async function invite(tenantId: string) {
    setInvitingId(tenantId);
    setInviteError(null);
    try {
      await api(`/tenants/${tenantId}/portal-invite`, { method: 'POST' });
      // Refetches rather than patching local state, so the new
      // `pendingInviteSentAt` comes from the server rather than being guessed.
      await invalidate({ resource: 'tenants', invalidates: ['list'] });
    } catch (caught) {
      setInviteError(caught instanceof ApiError ? caught.message : 'Could not send the invite.');
    } finally {
      setInvitingId(null);
    }
  }

  const columns: Column<TenantRow>[] = [
    {
      key: 'name',
      header: 'Tenant',
      render: (row) => (
        <>
          <strong>
            {row.lastName}, {row.firstName}
          </strong>
          {row.archivedAt ? (
            <>
              {' '}
              <span className="pill">Archived</span>
            </>
          ) : null}
          {row.achAccess.revokedAt ? (
            <>
              {' '}
              <span className="pill pill--late">ACH revoked</span>
            </>
          ) : null}
          {smsConsentById[row.id]?.status === 'DECLINED' ? (
            <>
              {' '}
              <span className="pill pill--late">SMS declined</span>
            </>
          ) : smsConsentById[row.id]?.status === 'NOT_ASKED' ? (
            <>
              {' '}
              <span className="pill">SMS not asked</span>
            </>
          ) : null}
          <div className="muted">{row.email}</div>
        </>
      ),
    },
    { key: 'phone', header: 'Phone', render: (row) => row.phone ?? '—' },
    { key: 'leases', header: 'Leases', numeric: true, render: (row) => row.leaseCount },
    {
      key: 'lease',
      header: 'Lease',
      // The lease this tenant is currently named on, in place of the forwarding
      // address — which unit/property/status a tenant belongs to is what staff
      // need at a glance day to day. Forwarding address only matters post
      // move-out and still lives on the tenant's own detail page.
      render: (row) =>
        row.currentLease ? (
          <>
            <div>
              <Link
                to={`/staff/leases/${row.currentLease.id}`}
                // The row itself jumps to the tenant's edit page; this jumps
                // to the lease instead, so it must not also trigger the row's
                // own click handler.
                onClick={(event) => event.stopPropagation()}
              >
                {row.currentLease.unitLabel}
              </Link>
            </div>
            <div className="muted" style={{ fontStyle: 'italic' }}>
              {row.currentLease.propertyName}
            </div>
            <span className={STATUS_PILL_CLASS[row.currentLease.status] ?? 'pill'}>
              {humanizeEnum(row.currentLease.status)}
            </span>
          </>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: 'portal',
      header: 'Portal',
      render: (row) => {
        if (row.hasPortalAccount) return <span className="pill">Has login</span>;
        if (row.pendingInviteSentAt)
          return <span className="pill pill--info">Awaiting registration</span>;
        if (!canWrite) return <span className="muted">No login</span>;
        const busy = invitingId === row.id;
        return (
          <button
            type="button"
            className="pill pill--action"
            disabled={busy}
            // A pill, not a row link — clicking it fires the invite rather
            // than navigating, so it must not also trigger the row's own
            // click handler.
            onClick={(event) => {
              event.stopPropagation();
              void invite(row.id);
            }}
          >
            {busy ? 'Sending…' : 'Invite to portal'}
          </button>
        );
      },
    },
  ];

  return (
    <section>
      <div className="row">
        <h1>Tenants</h1>
        <span className="shell__spacer" />
        {canWrite ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate('/staff/tenants/new')}
          >
            Add tenant
          </button>
        ) : null}
      </div>

      <div className="row">
        <input
          className="field__input"
          type="search"
          placeholder="Search name or email"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
        <label className="row">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => {
              setIncludeArchived(event.target.checked);
              setPage(1);
            }}
          />
          <span className="muted">Show archived</span>
        </label>
      </div>

      {inviteError ? <p className="alert alert--error">{inviteError}</p> : null}

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        onRowClick={(row) => void navigate(`/staff/tenants/${row.id}/edit`)}
        emptyMessage={search ? `No tenants match “${search}”.` : 'No tenants yet.'}
        pagination={{ page, pageSize, total: data?.total ?? 0, onPageChange: setPage }}
      />
    </section>
  );
}
